import dayjs from 'dayjs';

import type { FileEntryCondition } from '@/api/workflows';

import type { FieldValue } from './FieldRenderer';

/**
 * Evaluate one condition against the current form values. The values map is
 * keyed by field.id (the SOURCE field's id, not the gated target's). Returns
 * true when the condition is satisfied — i.e. the gated content should be
 * visible.
 *
 * Operators:
 *   equals       — string equality (after both sides are stringified)
 *   not_equals   — inverse of equals
 *   is_true      — truthy boolean / "true" string / numeric 1
 *   is_false     — opposite of is_true
 *   contains     — array source (CheckboxGroup) includes the target value
 *   not_contains — array source does NOT include the target value
 *   age_years_lt — Date source: computed age in whole years is < target
 *   age_years_ge — Date source: computed age in whole years is >= target
 *   count_eq     — Repeatable section source (fieldId = sectionId):
 *                  instance count == target
 *   count_ge     — Repeatable section source: instance count >= target
 *
 * **Unset semantics — operator-aware.** Positive-match operators (`equals`,
 * `contains`, `is_true`) treat an unset source as a non-match → hidden.
 * Negative-match operators (`not_equals`, `not_contains`, `is_false`) treat
 * an unset source as a match → visible. This matches the typical
 * conditional-form UX where dependent fields APPEAR as the advisor answers
 * upstream questions (rather than starting fully visible and shrinking),
 * AND keeps "show this unless they explicitly picked X" patterns working.
 *
 * Empty strings / arrays / NaN numbers all count as unset.
 */
function evaluateOne(c: FileEntryCondition, values: Record<string, FieldValue>): boolean {
  // Count operators short-circuit before the standard value lookup — the
  // condition's `fieldId` is a sectionId (not a workflow field id), and
  // the "value" the runtime checks is derived from the section's
  // `__instanceCount:<sectionId>` sentinel rather than a normal field
  // slot. Unset count defaults to 1 (matches the runtime renderer's
  // default of "one starter instance").
  if (c.operator === 'count_eq' || c.operator === 'count_ge') {
    // Reject a threshold that isn't a strict non-negative integer — a
    // stray character (`Number.parseInt('18abc')` returns 18) would
    // otherwise silently reinterpret a mis-authored threshold.
    if (!/^\d+$/.test(String(c.value ?? '').trim())) return false;
    const threshold = Number.parseInt(c.value, 10);
    if (!Number.isFinite(threshold)) return false;
    const rawCount = values[`__instanceCount:${c.fieldId}`];
    let count: number;
    if (typeof rawCount === 'number' && Number.isFinite(rawCount)) {
      count = rawCount;
    } else if (hasAnyInstanceKey(values, c.fieldId)) {
      // Renderer defaults an initialized-but-unsentinelled section to
      // one instance; treat this the same way for conditions.
      count = 1;
    } else {
      // No sentinel + no instance keys → the source id doesn't
      // reference a live repeatable section. Fail closed for
      // positive-match count operators so a misauthored condition
      // (originator picked a field id instead of a section id, or the
      // repeatable flag was later cleared) hides gated content rather
      // than silently matching count_ge=1.
      count = 0;
    }
    return c.operator === 'count_eq' ? count === threshold : count >= threshold;
  }
  // Instance-quantified source: the field lives inside a REPEATABLE section,
  // so there is no bare `values[fieldId]` to read — one value per instance
  // sits under `inst:<sectionId>:<idx>:<fieldId>`. Gather them and apply the
  // operator across the set.
  //
  // Zero instances is deliberately NOT a match for either quantifier: "every
  // beneficiary is a BC resident" must not be vacuously true when there are
  // no beneficiaries, or the gated question would appear on an empty form.
  if (c.instanceScope === 'any' || c.instanceScope === 'all') {
    const perInstance = collectInstanceValues(values, c.fieldId);
    if (perInstance.length === 0) return false;
    return c.instanceScope === 'any'
      ? perInstance.some((v) => applyOperator(c, v))
      : perInstance.every((v) => applyOperator(c, v));
  }

  return applyOperator(c, values[c.fieldId]);
}

/**
 * Apply one condition's operator to a single concrete value. Split out of
 * {@link evaluateOne} so instance-quantified conditions can run the same
 * comparison once per repeatable instance.
 */
function applyOperator(c: FileEntryCondition, v: FieldValue): boolean {
  const isSet = isScalarValueSet(v);

  // Unset source → operator-aware default. Positive-match operators expect a
  // concrete value to match against, so unset means "hide." Negative-match
  // operators (and `is_false`) expect the absence of a value, so unset means
  // "still visible."
  if (!isSet) {
    return (
      c.operator === 'not_equals' || c.operator === 'not_contains' || c.operator === 'is_false'
    );
  }

  switch (c.operator) {
    case 'is_true':
      return v === true || v === 'true' || v === 1;
    case 'is_false':
      return v === false || v === 'false' || v === 0;
    case 'equals':
      // SingleSelect / RadioGroup store the option's `value` string; we
      // compare on string identity so a numeric option still works.
      return String(v) === c.value;
    case 'not_equals':
      return String(v) !== c.value;
    case 'contains':
      // CheckboxGroup holds an array of option values. Stringify each entry
      // so a numeric option still matches a string-typed condition value.
      return Array.isArray(v) && v.some((item) => String(item) === c.value);
    case 'not_contains':
      return !Array.isArray(v) || !v.some((item) => String(item) === c.value);
    case 'age_years_lt':
    case 'age_years_ge': {
      // Source is a Date value stored as an ISO date string (YYYY-MM-DD).
      // Compute whole-year age at "today" — anything unparseable fails
      // closed (hidden) so a corrupted DOB doesn't accidentally reveal
      // sensitive downstream steps.
      const years = ageInWholeYears(v);
      if (years === null) return false;
      // Match the count-operator strict-integer parse — a stray suffix
      // on the threshold should fail closed, not silently truncate to a
      // valid-looking number via Number.parseInt('18abc').
      if (!/^\d+$/.test(String(c.value ?? '').trim())) return false;
      const threshold = Number.parseInt(c.value, 10);
      if (!Number.isFinite(threshold)) return false;
      return c.operator === 'age_years_lt' ? years < threshold : years >= threshold;
    }
    default:
      // Unknown operator — fail open (treat as visible) so an over-eager BE
      // future addition doesn't silently hide every gated field on this FE.
      return true;
  }
}

/**
 * Convert a stored Date value into whole years since today. Handles the
 * shape the runtime persists — an ISO 8601 date string emitted by the FE
 * Date picker's `dayjs(v).format('YYYY-MM-DD')`. Returns null when the
 * input isn't a parseable date, so the caller can treat unparseable DOBs
 * as "hidden" for positive-match operators.
 *
 * Delegates to dayjs to match the picker's timezone semantics — avoids
 * the `new Date('YYYY-MM-DD')` trap where the string parses as UTC
 * midnight but `.getFullYear()`/`.getMonth()`/`.getDate()` read locally,
 * shifting the birthday one day earlier in negative-UTC zones. On
 * 2026-05-14 in Toronto (UTC-4), DOB '2000-05-15' produced 26 years via
 * the naive Date math even though the real age is 25.
 */
/**
 * True when the values map has ANY `inst:<sectionId>:*` key. Used to
 * disambiguate "no sentinel and no data" (real repeatable section that
 * happens to be at its lazy default) from "no sentinel because the
 * fieldId doesn't reference a repeatable section at all" (misauthored
 * count condition).
 */
/**
 * True when a raw field value counts as "answered". Empty strings, NaN
 * numbers, and empty arrays all read as unset — the same rule the
 * condition evaluator applies before falling back to its operator-aware
 * unset default. Shared so the preview evaluator's "has the originator
 * answered this upstream field yet?" check can't drift from it.
 */
function isScalarValueSet(v: FieldValue): boolean {
  return (
    v !== null &&
    v !== undefined &&
    (typeof v !== 'string' || v.trim().length > 0) &&
    (typeof v !== 'number' || Number.isFinite(v)) &&
    (!Array.isArray(v) || v.length > 0)
  );
}

/**
 * True when a condition's SOURCE has a concrete value in `values` — i.e. the
 * user has actually answered the field the condition keys off. Count
 * operators key off the section's instance sentinel / instance keys rather
 * than a plain field slot, so they get their own liveness check.
 */
function isConditionSourceSet(c: FileEntryCondition, values: Record<string, FieldValue>): boolean {
  if (c.operator === 'count_eq' || c.operator === 'count_ge') {
    const raw = values[`__instanceCount:${c.fieldId}`];
    return (
      (typeof raw === 'number' && Number.isFinite(raw)) || hasAnyInstanceKey(values, c.fieldId)
    );
  }
  if (c.instanceScope === 'any' || c.instanceScope === 'all') {
    return collectInstanceValues(values, c.fieldId).some(isScalarValueSet);
  }
  return isScalarValueSet(values[c.fieldId]);
}

/**
 * Every repeatable instance's value for one authored field id, in instance
 * order.
 *
 * The evaluator only ever receives the flat values map, not the template, so
 * the owning section is recovered from the key shape
 * `inst:<sectionId>:<idx>:<fieldId>`. Field ids are UUIDs, so a suffix match
 * can't collide with another field.
 *
 * The instance COUNT deliberately comes from the section, not from the keys
 * this particular field happens to have: a beneficiary who filled in their
 * date of birth but not their province still exists, and must contribute an
 * `undefined` so an `all` quantifier doesn't read as satisfied. Counting only
 * the field's own keys silently skipped them.
 */
function collectInstanceValues(values: Record<string, FieldValue>, fieldId: string): FieldValue[] {
  const suffix = `:${fieldId}`;
  // Recover the owning section from any one of this field's instance keys.
  let sectionId: string | null = null;
  for (const key of Object.keys(values)) {
    if (!key.startsWith('inst:') || !key.endsWith(suffix)) continue;
    const parts = key.split(':');
    // inst : sectionId : idx : fieldId — section + field ids are UUIDs, so
    // a well-formed key always splits into exactly four segments.
    if (parts.length !== 4) continue;
    sectionId = parts[1] ?? null;
    if (sectionId) break;
  }
  if (sectionId === null) return [];

  const count = instanceCountFor(values, sectionId);
  const out: FieldValue[] = [];
  for (let i = 0; i < count; i++) out.push(values[`inst:${sectionId}:${i}:${fieldId}`]);
  return out;
}

/**
 * How many instances a repeatable section currently has, from the values map
 * alone. Prefers the sentinel the renderer maintains; falls back to the
 * highest instance index any of the section's keys mentions, so a section
 * that was populated without a sentinel still reports honestly.
 */
function instanceCountFor(values: Record<string, FieldValue>, sectionId: string): number {
  const sentinel = values[`__instanceCount:${sectionId}`];
  if (typeof sentinel === 'number' && Number.isFinite(sentinel)) {
    return Math.max(0, Math.floor(sentinel));
  }
  const prefix = `inst:${sectionId}:`;
  let maxIdx = -1;
  for (const key of Object.keys(values)) {
    if (!key.startsWith(prefix)) continue;
    const parts = key.split(':');
    if (parts.length !== 4) continue;
    const idx = Number.parseInt(parts[2] ?? '', 10);
    if (Number.isInteger(idx) && idx > maxIdx) maxIdx = idx;
  }
  return maxIdx + 1;
}

function hasAnyInstanceKey(values: Record<string, FieldValue>, sectionId: string): boolean {
  const prefix = `inst:${sectionId}:`;
  for (const k of Object.keys(values)) {
    if (k.startsWith(prefix)) return true;
  }
  return false;
}

function ageInWholeYears(v: unknown): number | null {
  if (typeof v !== 'string' || v.length === 0) return null;
  const parsed = dayjs(v);
  if (!parsed.isValid()) return null;
  const years = dayjs().diff(parsed, 'year');
  return Number.isFinite(years) ? years : null;
}

/**
 * Evaluate a list of conditions against the form values using the entry's
 * combinator ("all" = AND, "any" = OR). Empty conditions list is treated as
 * "always visible".
 */
export function evaluateConditions(
  conditions: FileEntryCondition[] | undefined | null,
  combinator: 'all' | 'any' | null | undefined,
  values: Record<string, FieldValue>,
): boolean {
  if (!conditions || conditions.length === 0) return true;
  if (combinator === 'any') {
    return conditions.some((c) => evaluateOne(c, values));
  }
  // Default to "all" — matches the domain default when the BE rejected an
  // unspecified combinator (we never persist non-empty conditions without
  // one, but the fallback keeps the renderer safe).
  return conditions.every((c) => evaluateOne(c, values));
}

/**
 * Preview-mode visibility for a SECTION. Mirrors {@link evaluateConditions}
 * with one carve-out: a CROSS-STEP condition (source lives on a step other
 * than `ownStepId`) whose source is still UNSET is treated as satisfied.
 *
 * Rationale: the originator preview must not hide a downstream section merely
 * because an upstream branch selector hasn't been answered yet — a gated tab
 * would otherwise present as a blank page. But once the originator DOES answer
 * that upstream field, the branch should be honored (a section gated
 * `Beneficiary Type is_not RESP` must disappear once RESP is picked). Same-step
 * conditions are never excused — those are inline reveal patterns that are
 * meant to start hidden.
 *
 * The excuse is applied per-condition BEFORE the combinator, so an "all"/"any"
 * rule with a mix of answered and unanswered cross-step sources still composes
 * correctly.
 */
export function evaluateConditionsForPreview(
  conditions: FileEntryCondition[] | undefined | null,
  combinator: 'all' | 'any' | null | undefined,
  values: Record<string, FieldValue>,
  ownStepId: string,
): boolean {
  if (!conditions || conditions.length === 0) return true;
  const satisfiedOrExcused = (c: FileEntryCondition): boolean =>
    evaluateOne(c, values) ||
    // Cross-step AND not yet answered → excuse it (don't let an unanswered
    // upstream field blank out the preview tab).
    (c.fieldStepId !== ownStepId && !isConditionSourceSet(c, values));
  if (combinator === 'any') {
    return conditions.some(satisfiedOrExcused);
  }
  return conditions.every(satisfiedOrExcused);
}
